const VALID_TICKER = /^[A-Z0-9.-]{1,15}$/;
export const CENTRAL_TIME = "America/Chicago";
export const formatCentral = (value) => value ? new Intl.DateTimeFormat("en-US", { timeZone: CENTRAL_TIME, dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";
export const normalizeFormation = (value) => String(value || "").trim().replace(/\s+/g, " ").replace(/\s+forming$/i, "").toUpperCase();
export const candleFormationLabel = (item) => item?.candle_state || "";
export const chartPatternLabel = (item) => item?.chart_state || "";
export const formationLabel = (item) => candleFormationLabel(item) || chartPatternLabel(item);
export const formationClass = (item) => item?.direction === "UP" ? "BULLISH" : item?.direction === "DOWN" ? "BEARISH" : "NEUTRAL";

export function decisionQualifier(structure) {
  const frames = structure?.detail?.guru_msi?.timeframes || [];
  const direction = structure?.direction || "WAIT";
  const short = frames.filter((x) => ["DAILY", "4H"].includes(x.timeframe)).map((x) => x.direction);
  const broad = frames.filter((x) => ["WEEKLY", "3D"].includes(x.timeframe)).map((x) => x.direction);
  if (direction === "UP" && short.includes("DOWN")) return "UP — Pullback Risk";
  if (direction === "DOWN" && short.includes("UP")) return "DOWN — Bounce Risk";
  if (direction === "WAIT") return "WAIT — Timeframes Mixed";
  if ([...short, ...broad].filter((x) => x !== "WAIT").every((x) => x === direction)) return `${direction} — Timeframes Aligned`;
  return `${direction} — Timeframes Mixed`;
}

export function conflictSummary(structure) {
  const frames = structure?.detail?.guru_msi?.timeframes || [];
  const byFrame = Object.fromEntries(frames.map((x) => [x.timeframe, x]));
  const direction = structure?.direction;
  const shortAgainst = ["DAILY", "4H"].filter((key) => byFrame[key]?.direction && byFrame[key].direction !== "WAIT" && byFrame[key].direction !== direction);
  const broadSupports = ["WEEKLY", "3D"].filter((key) => byFrame[key]?.direction === direction);
  const formationAgainst = shortAgainst.find((key) => formationLabel(byFrame[key]));
  const messages = [];
  if (shortAgainst.length) messages.push(`${shortAgainst.join(" and ")} weakening against the broader ${direction} structure`);
  if (formationAgainst) messages.push(`${formationClass(byFrame[formationAgainst]).replace("BULLISH", "Bullish").replace("BEARISH", "Bearish").replace("NEUTRAL", "Neutral")} formation developing against broader ${direction} structure`);
  if (broadSupports.length) messages.push(`Longer-term ${broadSupports.join(" and ")} structure remains ${direction}`);
  return messages;
}

export function formationTiming(item, now = Date.now(), closingSoonMinutes = 30, maxAgeMinutes = 10, conflicted = false) {
  const close = Date.parse(item?.candle_expected_close || "");
  const asOf = Date.parse(item?.as_of || "");
  const minutesToClose = Number.isFinite(close) ? Math.ceil((close - now) / 60000) : null;
  const ageMinutes = Number.isFinite(asOf) ? Math.max(0, Math.floor((now - asOf) / 60000)) : null;
  const freshForClose = ageMinutes !== null && ageMinutes <= maxAgeMinutes;
  const closingSoon = item?.candle_status === "FORMING" && freshForClose && minutesToClose >= 0 && minutesToClose <= closingSoonMinutes;
  const state = item?.candle_status === "CONFIRMED" ? "CONFIRMED" : conflicted ? "FORMING - CONFLICTED" : closingSoon ? "FORMING - CLOSING SOON" : "FORMING - EARLY";
  return { state, minutesToClose, ageMinutes, freshForClose, closingSoon, nextRefreshNeededBy: Number.isFinite(asOf) ? new Date(asOf + maxAgeMinutes * 60000).toISOString() : null };
}

export function tradingViewUrl(structure) {
  const symbol = String(structure?.detail?.chart?.symbol || "").trim().toUpperCase();
  const match = symbol.match(/^([A-Z0-9._-]{1,20}):([A-Z0-9.-]{1,15})$/);
  if (!match) return null;
  const [, exchange, ticker] = match;
  return `https://www.tradingview.com/symbols/${exchange}-${ticker}/`;
}

export function resetMsiDetailScroll(root) {
  if (typeof window !== "undefined") window.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
  if (typeof document !== "undefined" && document.scrollingElement) document.scrollingElement.scrollTop = 0;
  let node = root?.parentElement;
  while (node) {
    if (node.scrollHeight > node.clientHeight) node.scrollTop = 0;
    node = node.parentElement;
  }
}

export function movingAverageDistance(price, average) {
  if (price === null || price === undefined || price === "" || average === null || average === undefined || average === "") return null;
  const current = Number(price); const basis = Number(average);
  if (!Number.isFinite(current) || !Number.isFinite(basis) || basis === 0) return null;
  const value = ((current - basis) / basis) * 100;
  return { value, label: `${Math.abs(value) < 0.005 ? "" : value > 0 ? "+" : "−"}${Math.abs(value).toFixed(2)}%`, tone: Math.abs(value) < 0.005 ? "equal" : value > 0 ? "above" : "below" };
}

export function sourceSemantics(contract, now = Date.now()) {
  const source = Date.parse(contract?.source_as_of || "");
  const daily = contract?.structures?.flatMap((row) => row.detail?.guru_msi?.timeframes || []).find((item) => item.timeframe === "DAILY");
  const close = Date.parse(daily?.candle_expected_close || contract?.market_close || "");
  const sourceParts = Number.isFinite(source) ? Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(source)).map((part) => [part.type, part.value])) : null;
  const sourceSession = sourceParts ? `${sourceParts.year}-${sourceParts.month}-${sourceParts.day}` : null;
  const governedClosedSession = contract?.generation_mode === "CLOSED"
    && contract?.freshness_status === "CURRENT"
    && sourceSession === contract?.latest_completed_session;
  // Intraday provider indexes identify the opening instant of the final bar.
  // A validated CLOSED contract is already bound to the exchange calendar's
  // completed session, so it is completed-close data even when that bar-start
  // timestamp precedes market_close.
  const completedClose = governedClosedSession || (Number.isFinite(source) && Number.isFinite(close) && source >= close);
  const ageMinutes = Number.isFinite(source) ? Math.max(0, Math.floor((now - source) / 60000)) : null;
  return { sourceKind: completedClose ? "COMPLETED CLOSE" : "INTRADAY SNAPSHOT", completedClose, ageMinutes };
}
